import type { FC, FormEvent, ReactNode } from 'react'

import { useEffect, useId, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, ChevronDown, CircleCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isValidEmail } from '@/lib/format'
import { submitInquiry } from '@/lib/inquiries'
import { Button } from '@/components/generals/button'

export const CONTACT_TOPICS: Array<string> = [
    "Managed Flows",
    "Flow Studio",
    "Pilot",
    "Open API",
    "A new connection",
    "Support for a live flow",
    "Partnership",
    "Something else"
]

interface ContactFormProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    topic: string
    onTopicChange: (topic: string) => void
    initialMessage?: string
}

interface FieldErrors {
    name?: string
    email?: string
    message?: string
}

const inputClasses = 'w-full rounded-2xl border border-ink/15 bg-paper px-4 text-base text-ink sm:text-[15px] transition-[border-color,box-shadow] placeholder:text-stone/80 focus:border-ultramarine/50 focus:outline-none focus:ring-4 focus:ring-ultramarine/10 aria-[invalid=true]:border-[#c8352b]/60'

/** The plain fallback form, tucked behind "Prefer a form?" so the page works without the chat. */
export const ContactForm: FC<ContactFormProps> = ({
    open,
    onOpenChange,
    topic,
    onTopicChange,
    initialMessage = ''
}): ReactNode => {
    const id = useId()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [company, setCompany] = useState('')
    const [message, setMessage] = useState(initialMessage)
    const [website, setWebsite] = useState('')
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
    const [serverError, setServerError] = useState<string | null>(null)

    useEffect(() => {
        if (initialMessage) setMessage(current => current || initialMessage)
    }, [initialMessage])

    const validate = (): FieldErrors => {
        const next: FieldErrors = {}

        if (!name.trim()) next.name = 'Please tell us your name.'
        if (!isValidEmail(email)) next.email = 'Please enter a valid work email.'
        if (message.trim().length < 10) next.message = 'A sentence or two helps us route this to the right person.'

        return next
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const next = validate()

        setErrors(next)

        if (Object.keys(next).length > 0) {
            const first = Object.keys(next)[0]

            document.getElementById(`${id}-${first}`)?.focus()
            return
        }

        setStatus('sending')
        setServerError(null)

        try {
            await submitInquiry({
                source: 'contact-form',
                name: name.trim(),
                email: email.trim(),
                company: company.trim(),
                topic,
                message: message.trim(),
                website
            })

            setStatus('sent')
        }
        catch (error) {
            setStatus('error')
            setServerError(error instanceof Error ? error.message : "We couldn't send that just now.")
        }
    }

    return (
        <div className='surface rounded-[28px]'>
            <button
                type='button'
                aria-expanded={open}
                aria-controls={`${id}-panel`}
                onClick={() => onOpenChange(!open)}
                className='flex w-full items-center justify-between gap-4 rounded-[28px] px-5 py-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine sm:gap-6 sm:px-8'
            >
                <span>
                    <span className='block font-heading text-xl font-semibold tracking-[-0.025em] text-ink'>
                        Prefer a form?
                    </span>

                    <span className='mt-1 block text-[14px] text-stone'>
                        Same people, same reply time. Takes about a minute.
                    </span>
                </span>

                <span className={cn('grid size-10 shrink-0 place-items-center rounded-full border border-ink/15 transition-transform duration-300', open && 'rotate-180')}>
                    <ChevronDown aria-hidden='true' className='size-5' />
                </span>
            </button>

            <div
                id={`${id}-panel`}
                className={cn('grid transition-[grid-template-rows] duration-500 ease-calm', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
            >
                <div className='overflow-hidden' inert={!open}>
                    <div className='px-5 pb-8 pt-2 sm:px-8'>
                        {status === 'sent' ? (
                            <div role='status' className='flex flex-col items-start gap-4 py-6'>
                                <CircleCheck aria-hidden='true' className='size-8 text-ultramarine' strokeWidth={1.6} />

                                <p className='font-heading text-2xl font-semibold tracking-[-0.025em] text-ink'>
                                    Thanks, a person will reply within one working day.
                                </p>

                                <p className='break-words text-[15px] text-ink-soft'>
                                    {`We'll write to ${email.trim()}. If it's urgent, call the number above.`}
                                </p>
                            </div>
                        ) : (
                            <form noValidate onSubmit={event => void onSubmit(event)} className='grid gap-5 sm:grid-cols-2'>
                                <div className='hidden' aria-hidden='true'>
                                    <label htmlFor={`${id}-website`}>
                                        Leave this field empty
                                    </label>

                                    <input
                                        id={`${id}-website`}
                                        tabIndex={-1}
                                        autoComplete='off'
                                        value={website}
                                        onChange={event => setWebsite(event.target.value)}
                                    />
                                </div>

                                <div>
                                    <label htmlFor={`${id}-name`} className='mb-2 block text-sm font-medium text-ink'>
                                        Name
                                    </label>

                                    <input
                                        id={`${id}-name`}
                                        value={name}
                                        onChange={event => setName(event.target.value)}
                                        autoComplete='name'
                                        maxLength={120}
                                        aria-invalid={Boolean(errors.name)}
                                        aria-describedby={errors.name ? `${id}-name-error` : undefined}
                                        className={cn(inputClasses, 'h-12')}
                                    />

                                    {errors.name && (
                                        <p id={`${id}-name-error`} className='mt-1.5 text-[13px] text-[#c8352b]'>
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor={`${id}-email`} className='mb-2 block text-sm font-medium text-ink'>
                                        Work email
                                    </label>

                                    <input
                                        id={`${id}-email`}
                                        type='email'
                                        value={email}
                                        onChange={event => setEmail(event.target.value)}
                                        autoComplete='email'
                                        maxLength={200}
                                        aria-invalid={Boolean(errors.email)}
                                        aria-describedby={errors.email ? `${id}-email-error` : undefined}
                                        className={cn(inputClasses, 'h-12')}
                                    />

                                    {errors.email && (
                                        <p id={`${id}-email-error`} className='mt-1.5 text-[13px] text-[#c8352b]'>
                                            {errors.email}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor={`${id}-company`} className='mb-2 block text-sm font-medium text-ink'>
                                        Company <span className='font-normal text-stone'>(optional)</span>
                                    </label>

                                    <input
                                        id={`${id}-company`}
                                        value={company}
                                        onChange={event => setCompany(event.target.value)}
                                        autoComplete='organization'
                                        maxLength={160}
                                        className={cn(inputClasses, 'h-12')}
                                    />
                                </div>

                                <div>
                                    <label htmlFor={`${id}-topic`} className='mb-2 block text-sm font-medium text-ink'>
                                        Topic
                                    </label>

                                    <div className='relative'>
                                        <select
                                            id={`${id}-topic`}
                                            value={topic}
                                            onChange={event => onTopicChange(event.target.value)}
                                            className={cn(inputClasses, 'h-12 appearance-none pr-10')}
                                        >
                                            {CONTACT_TOPICS.map(item => (
                                                <option key={item} value={item}>
                                                    {item}
                                                </option>
                                            ))}
                                        </select>

                                        <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-stone' />
                                    </div>
                                </div>

                                <div className='sm:col-span-2'>
                                    <label htmlFor={`${id}-message`} className='mb-2 block text-sm font-medium text-ink'>
                                        What is stuck?
                                    </label>

                                    <textarea
                                        id={`${id}-message`}
                                        value={message}
                                        onChange={event => setMessage(event.target.value)}
                                        rows={5}
                                        maxLength={5000}
                                        placeholder='What you ship, which systems are involved, and what goes wrong today.'
                                        aria-invalid={Boolean(errors.message)}
                                        aria-describedby={errors.message ? `${id}-message-error` : undefined}
                                        className={cn(inputClasses, 'resize-y py-3 leading-relaxed')}
                                    />

                                    {errors.message && (
                                        <p id={`${id}-message-error`} className='mt-1.5 text-[13px] text-[#c8352b]'>
                                            {errors.message}
                                        </p>
                                    )}
                                </div>

                                <div className='flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between'>
                                    <p className='text-[13px] text-stone'>
                                        We only use your details to reply. See our <Link href='/privacy' className='underline underline-offset-2 hover:text-ink'>privacy policy</Link>.
                                    </p>

                                    <Button type='submit' size='lg' disabled={status === 'sending'} className='w-full sm:w-auto'>
                                        {status === 'sending' ? 'Sending...' : 'Send message'}
                                        {status !== 'sending' && <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/btn:translate-x-0.5' />}
                                    </Button>
                                </div>

                                {status === 'error' && serverError && (
                                    <p role='alert' className='rounded-2xl bg-[#fbe7e3] px-4 py-3 text-[14px] text-[#9d2b20] sm:col-span-2'>
                                        {serverError}
                                    </p>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
